import React, { use, useEffect, useState } from 'react';
import StarShape from './components/StarShape';
import InnerCircle from './components/InnerCircle';
import Tile from './components/Tile';
import TileGroup from './components/TileGroup';
import Professional from './components/Professional';
import Academic from './components/Academic';
import Personal from './components/Personal';
import Projects from './components/Projects';
import LeftSidebar from './components/LeftSidebar';
import { SvgZoomContextProvider } from './contexts/SvgZoomContext';
import './Home.css';
import { registry } from './registryData';

interface ComponentConfig {
    component: React.ComponentType<any>;
    props: {
        cx?: number;
        cy?: number;
        r?: number;
        tx?: number;
        ty?: number;
        texture?: string;
        specialTiles?: ComponentConfig[];
        facadeTiles?: [number, number][];
        groupCoordinates?: [number, number][];
        onclick?: () => void;
    };
    key: string;
}

const Home: React.FC = () => {
    const [tileConfigs, setTileConfigs] = useState<ComponentConfig[]>([]);

    useEffect(() => {
        console.log("Home component mounted");
        loadTileConfigurations();
    }, []);

    const loadTileConfigurations = () => {
        Promise.all([
            fetch('/worlds/front.json').then(res => res.json()),
            fetch('/worlds/special.json').then(res => res.json()),
            fetch('/worlds/mid.json').then(res => res.json()).catch(() => []) // Handle missing mid.json
        ])
        .then(([frontData, specialData, midData]) => {
            // Create lookup maps
            const specialLookup = new Map<string, string>();
            specialData.forEach((item: any) => {
                const posKey = `${item.props.tx},${item.props.ty}`;
                specialLookup.set(posKey, item.props.texture);
            });

            const midLookup = new Set<string>();
            midData.forEach((item: any) => {
                const posKey = `${item.props.tx},${item.props.ty}`;
                midLookup.add(posKey);
            });

            // Categorize tiles based on special colors
            const regularTiles: ComponentConfig[] = [];
            const specialGroups: Record<string, ComponentConfig[]> = {};
            const facadeGroups: Record<string, [number, number][]> = {};
            const groupDefinitions: Record<string, [number, number][]> = {};
            for (const key of Object.keys(registry)) {
                const texture = registry[key]["group-definition-texture"];
                specialGroups[texture] = [];
                facadeGroups[texture] = [];
                groupDefinitions[texture] = [];
            }
            // First, populate group definitions from ALL special data coordinates
            specialData.forEach((item: any) => {
                const texture = item.props.texture;
                if (groupDefinitions[texture]) {
                    groupDefinitions[texture].push([item.props.tx, item.props.ty]);
                    
                    const posKey = `${item.props.tx},${item.props.ty}`;
                    // Check if this coordinate is NOT in mid.json (facade coordinate)
                    if (!midLookup.has(posKey)) {
                        facadeGroups[texture].push([item.props.tx, item.props.ty]);
                    }
                }
            });

            // Then process front data for rendering, categorizing by special textures
            frontData.forEach((item: any) => {
                const posKey = `${item.props.tx},${item.props.ty}`;
                const specialTexture = specialLookup.get(posKey);
                
                const config: ComponentConfig = {
                    component: Tile,
                    props: item.props,
                    key: item.key
                };

                // Check if this position has a special colored wool texture
                if (specialTexture && specialGroups[specialTexture]) {
                    specialGroups[specialTexture].push(config);
                } else {
                    regularTiles.push(config);
                }
            });

            // Create TileGroup components for each wool color
            const tileGroupConfigs: ComponentConfig[] = [];
            
            for (const key of Object.keys(registry)){
                const texture = registry[key]["group-definition-texture"];
                tileGroupConfigs.push({
                    component: registry[key].component,
                    props: {
                        specialTiles: specialGroups[texture] || [],
                        facadeTiles: facadeGroups[texture] || [],
                        groupCoordinates: groupDefinitions[texture] || []
                    },
                    key: `${key}-${texture.split('/').pop()?.split('_')[0]}`
                });
            }

            // Combine all tile configs
            setTileConfigs([...tileGroupConfigs,...regularTiles]);
            
            console.log('Special tile groups:', specialGroups);
            console.log('Facade coordinates:', facadeGroups);
            console.log('Group definitions:', groupDefinitions);
        })
        .catch(err => {
            console.error('Error loading tile configurations:', err);
            // Fallback to default tile configuration
            setTileConfigs([{
                component: Tile,
                props: { tx: 0, ty: 0, texture: '/textures/dirt.png' },
                key: 'fallback-tile'
            }]);
        });
    };

    const worldSizeX = 1024;
    const worldSizeY = 1024;

    return (
        <div className="home-container">
            <LeftSidebar showRegistryMenu={true} />
            <div className="svg-world">
                <SvgZoomContextProvider height={worldSizeY} width={worldSizeX}>
                    {tileConfigs.map(({ component: Component, props, key }) => (
                        <Component key={key} {...props} />
                    ))}
                </SvgZoomContextProvider>
            </div>
        </div>
    );
};

export default Home;